import { c as _c } from "react/compiler-runtime";
import { centsToUnits } from '@systemeio/ui';
import LazyContactInfoTooltip from 'modules/contacts/components/lazy-contact-info-tooltip';
import { getFunnelStepUrl } from 'modules/funnels/funnel/utils/get-funnel-step-url';
import { mainEventsDescription, MainEventsEnum, mainEventsType } from 'modules/main/enums/main-events-enum';
import { EventChargeInterface, EventPaymentInterface, MainEventType } from 'modules/main/types/main-interface';
import React, { type JSX } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { getBlogPostNextjsUrl } from 'shared/utils/get-blog-post-nextjs-url';
import { MoneyCancelIcon, MoneyReturnIcon, MoneySucceedIcon, PersonIcon } from '@systemeio/ui/icons';
interface RenderEventProps {
  event: MainEventType;
  lastItem?: boolean;
}
const eventIcon: Record<MainEventsEnum, JSX.Element> = {
  [MainEventsEnum.contact_created]: <PersonIcon className={'h-[24px] w-[24px] fill-[#35E190]'} />,
  [MainEventsEnum.new_signup]: <PersonIcon className={'h-[24px] w-[24px] fill-[#35E190]'} />,
  [MainEventsEnum.recurring_charge_succeed]: <MoneyReturnIcon />,
  [MainEventsEnum.one_shot_charge_succeed]: <MoneySucceedIcon />,
  [MainEventsEnum.payment_subscription_stopped]: <MoneyCancelIcon />,
  [MainEventsEnum.payment_subscription_completed]: <MoneyReturnIcon />,
  [MainEventsEnum.payment_subscription_created]: <MoneyReturnIcon />,
  [MainEventsEnum.recurring_charge_failed]: <MoneyCancelIcon />
};
function RenderEvent(t0) {
  const $ = _c(37);
  const {
    event,
    lastItem
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const getOfferName = _temp;
  const getCustomerName = _temp2;
  const createContactTooltip = _temp3;
  const createFunnelStepLink = _temp4;
  let t1;
  if ($[0] !== event || $[1] !== t || $[2] !== user?.dashboardLocale) {
    t1 = () => {
      switch (event.type) {
        case MainEventsEnum.new_signup:
          {
            const {
              relatedObject: relatedObject_1
            } = event;
            return <TransLoco t={t} i18nKey={mainEventsDescription[event.type]} values={{
              email: relatedObject_1.email
            }} components={{
              contactLink: createContactTooltip(relatedObject_1.id, relatedObject_1.removedAt)
            }} />;
          }
        case MainEventsEnum.recurring_charge_succeed:
        case MainEventsEnum.one_shot_charge_succeed:
          {
            const {
              relatedObject: relatedObject_0
            } = event;
            const {
              orderItem: orderItem_1,
              payment,
              amount
            } = relatedObject_0;
            const {
              customer: customer_1
            } = orderItem_1.order;
            const contact_0 = customer_1.contact;
            return <TransLoco t={t} i18nKey={mainEventsDescription[event.type]} values={{
              price: currencyFormat(centsToUnits(amount), payment.currency, user?.dashboardLocale),
              offer_name: getOfferName(orderItem_1),
              customer_name: getCustomerName(customer_1)
            }} components={{
              contactLink: createContactTooltip(contact_0.id, contact_0.removedAt),
              a: createFunnelStepLink(orderItem_1.offer)
            }} />;
          }
        case MainEventsEnum.payment_subscription_created:
        case MainEventsEnum.recurring_charge_failed:
        case MainEventsEnum.payment_subscription_stopped:
        case MainEventsEnum.payment_subscription_completed:
          {
            const {
              orderItem: orderItem_0
            } = event.relatedObject;
            const {
              customer: customer_0
            } = orderItem_0.order;
            const contact = customer_0.contact;
            return <TransLoco t={t} i18nKey={mainEventsDescription[event.type]} values={{
              customer_name: getCustomerName(customer_0),
              offer_name: getOfferName(orderItem_0)
            }} components={{
              a: createContactTooltip(contact.id, contact.removedAt),
              b: createFunnelStepLink(orderItem_0.offer)
            }} />;
          }
        case MainEventsEnum.contact_created:
          {
            const {
              relatedObject
            } = event;
            const {
              sourcePage,
              email,
              id,
              removedAt: removedAt_0,
              pageTitle
            } = relatedObject;
            const contactTooltip = createContactTooltip(id, removedAt_0);
            if (!sourcePage) {
              return <TransLoco t={t} i18nKey="dashboard.main.event.message.contact_created_without_source_page" values={{
                email
              }} components={{
                contactLink: contactTooltip
              }} />;
            }
            const getHref = () => {
              if (sourcePage.funnelStep) {
                return getFunnelStepUrl(sourcePage.funnelStep.funnel.id, sourcePage.funnelStep.id);
              }
              if (sourcePage.blogPage) {
                return getBlogPostNextjsUrl(sourcePage.blogPage.blog.id);
              }
              return "";
            };
            const sourcePageLink = <LinkWithoutPrefetch className="hover:text-text-tertiary/70 underline" href={getHref()} />;
            return <TransLoco t={t} i18nKey={mainEventsDescription[event.type]} values={{
              email,
              form: pageTitle
            }} components={{
              a: contactTooltip,
              b: sourcePageLink
            }} />;
          }
      }
    };
    $[0] = event;
    $[1] = t;
    $[2] = user?.dashboardLocale;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getEventElement = t1;
  const t2 = `${lastItem ? "pt-4" : "py-[15px]"} font-averta flex gap-2.5`;
  const t3 = eventIcon[event.type];
  let t4;
  if ($[4] !== t3) {
    t4 = <div className="[&>svg]:scale-75 flex h-[24px] min-w-[28px] items-center justify-center rounded-full">{t3}</div>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = mainEventsType[event.type];
  let t6;
  if ($[6] !== t || $[7] !== t5) {
    t6 = t(t5);
    $[6] = t;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <span className="font-bold text-[#414355]">{t6}</span>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = user?.timeFormat;
  const t9 = user?.dateFormat;
  const t10 = user?.timezone;
  let t11;
  if ($[11] !== event.createdAt || $[12] !== t10 || $[13] !== t8 || $[14] !== t9) {
    t11 = dateTimeFormat(event.createdAt, {
      timeFormat: t8,
      dateFormat: t9,
      timeZone: t10
    });
    $[11] = event.createdAt;
    $[12] = t10;
    $[13] = t8;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11) {
    t12 = <span className="text-[rgb(var(--text-secondary))] truncate text-xs">{t11}</span>;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t7) {
    t13 = <div className="flex items-center justify-between gap-3">{t7}{t12}</div>;
    $[18] = t12;
    $[19] = t7;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== getEventElement) {
    t14 = getEventElement();
    $[21] = getEventElement;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14) {
    t15 = <div className="flex flex-wrap gap-1 truncate text-xs leading-normal">{t14}</div>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t13 || $[26] !== t15) {
    t16 = <div className="flex w-full flex-col gap-1 truncate">{t13}{t15}</div>;
    $[25] = t13;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t16 || $[29] !== t2 || $[30] !== t4) {
    t17 = <div className={t2}>{t4}{t16}</div>;
    $[28] = t16;
    $[29] = t2;
    $[30] = t4;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== lastItem) {
    t18 = !lastItem && <hr className="color-text-primary opacity-10" />;
    $[32] = lastItem;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t17 || $[35] !== t18) {
    t19 = <>{t17}{t18}</>;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
}
function _temp4(offer) {
  if (!offer?.funnelStep) {
    return <span />;
  }
  return <LinkWithoutPrefetch className="hover:text-text-tertiary/70 truncate underline" href={getFunnelStepUrl(offer.funnelStep.funnel.id, offer.funnelStep.id)} />;
}
function _temp3(contactId, removedAt) {
  return <LazyContactInfoTooltip contactId={contactId} removedAt={removedAt} />;
}
function _temp2(customer) {
  return customer.fullName || customer.contact.connectedUser?.fullName || customer.contact.email;
}
function _temp(orderItem) {
  if (orderItem.pricePlan) {
    return orderItem.pricePlan.innerName;
  }
  if (orderItem.productOrderItem) {
    return orderItem.productOrderItem.product.name;
  }
  return "";
}
export default RenderEvent;